import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { adminApi } from '../../services/marketplaceApi';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';
import '../../styles/marketplace.css';

const TABS = [
    { id: 'products', path: '/admin/marketplace/products' },
    { id: 'purchases', path: '/admin/marketplace/purchases' },
    { id: 'verification', path: '/admin/marketplace/verification' },
    { id: 'analytics', path: '/admin/marketplace/analytics' },
];

const emptyProduct = {
    code: '', name: '', description: '', productType: 'subscription', targetType: 'all',
    durationDays: 30, priceRub: 0, priceUsd: 0, priceAmd: 0, featuresJson: '{}', rotationSlots: 10, sortOrder: 0, isActive: true,
};

export default function MarketplaceAdminPage() {
    const { t } = useTranslation(['marketplace', 'admin', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const tab = useMemo(() => TABS.find((x) => location.pathname.startsWith(x.path))?.id || 'products', [location.pathname]);

    const [products, setProducts] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [verifications, setVerifications] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [editId, setEditId] = useState(null);
    const [note, setNote] = useState('');
    const [selectedReq, setSelectedReq] = useState(null);

    const loadProducts = useCallback(async () => {
        const { data } = await adminApi().get('/admin/marketplace/products');
        setProducts(data || []);
    }, []);

    const loadPurchases = useCallback(async () => {
        const { data } = await adminApi().get('/admin/marketplace/purchases');
        setPurchases(data.items || []);
    }, []);

    const loadVerifications = useCallback(async () => {
        const { data } = await axios.get(`${getApiBaseUrl()}/admin/verification`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            params: { status: 'pending' },
        });
        setVerifications(data.items || []);
    }, []);

    const loadAnalytics = useCallback(async () => {
        const { data } = await adminApi().get('/admin/marketplace/analytics');
        setAnalytics(data);
    }, []);

    useEffect(() => {
        if (tab === 'products') loadProducts();
        if (tab === 'purchases') loadPurchases();
        if (tab === 'verification') loadVerifications();
        if (tab === 'analytics') loadAnalytics();
    }, [tab, loadProducts, loadPurchases, loadVerifications, loadAnalytics]);

    async function saveProduct(e) {
        e.preventDefault();
        if (editId) await adminApi().put(`/admin/marketplace/products/${editId}`, form);
        else await adminApi().post('/admin/marketplace/products', form);
        setForm(emptyProduct);
        setEditId(null);
        loadProducts();
    }

    async function modAction(id, action) {
        const api = axios.create({ baseURL: getApiBaseUrl(), headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (action === 'approve') await api.post(`/admin/verification/${id}/approve`, { note });
        if (action === 'reject') await api.post(`/admin/verification/${id}/reject`, { note });
        if (action === 'docs') await api.post(`/admin/verification/${id}/request-docs`, { note });
        setSelectedReq(null);
        setNote('');
        loadVerifications();
    }

    return (
        <OwnerPageLayout title={t('marketplace:admin.title')} maxWidth={1100}>
            <div className="mp-admin-tabs">
                {TABS.map((x) => (
                    <button key={x.id} type="button" className={tab === x.id ? 'active' : ''} onClick={() => navigate(x.path)}>{t(`marketplace:admin.tabs.${x.id}`)}</button>
                ))}
            </div>

            {tab === 'products' && (
                <div>
                    <form onSubmit={saveProduct} className="sp-panel" style={{ marginBottom: 16 }}>
                        <div className="mp-form-grid">
                            <label>Code<input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required /></label>
                            <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
                            <label>Days<input type="number" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })} /></label>
                            <label>RUB<input type="number" value={form.priceRub} onChange={(e) => setForm({ ...form, priceRub: Number(e.target.value) })} /></label>
                            <label>USD<input type="number" value={form.priceUsd} onChange={(e) => setForm({ ...form, priceUsd: Number(e.target.value) })} /></label>
                            <label>Slots<input type="number" value={form.rotationSlots} onChange={(e) => setForm({ ...form, rotationSlots: Number(e.target.value) })} /></label>
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 12 }}>{editId ? t('common:save') : t('common:add')}</button>
                    </form>
                    <table className="mp-table">
                        <thead><tr><th>Code</th><th>Name</th><th>Days</th><th>RUB</th><th>Slots</th><th></th></tr></thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.code}</td><td>{p.name}</td><td>{p.durationDays}</td><td>{p.priceRub}</td><td>{p.rotationSlots}</td>
                                    <td><button type="button" className="sp-btn sp-btn--outline" onClick={() => { setEditId(p.id); setForm(p); }}>{t('common:edit')}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button type="button" className="sp-btn sp-btn--outline" style={{ marginTop: 12 }} onClick={() => adminApi().post('/admin/marketplace/purchases/rotate')}>{t('marketplace:admin.rotateQueue')}</button>
                </div>
            )}

            {tab === 'purchases' && (
                <table className="mp-table">
                    <thead><tr><th>ID</th><th>User</th><th>Product</th><th>Status</th><th>Expires</th><th>Queue</th></tr></thead>
                    <tbody>
                        {purchases.map((p) => (
                            <tr key={p.id}>
                                <td>{p.id}</td><td>{p.userName || p.userId}</td><td>{p.productName}</td><td>{p.status}</td><td>{p.expiresAt?.slice(0, 10)}</td><td>{p.queuePosition}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {tab === 'verification' && (
                <div>
                    <table className="mp-table">
                        <thead><tr><th>ID</th><th>Type</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            {verifications.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.id}</td><td>{r.entityType}</td><td>{r.status}</td>
                                    <td><button type="button" className="sp-btn sp-btn--outline" onClick={() => setSelectedReq(r)}>{t('common:open')}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {selectedReq && (
                        <div className="sp-panel" style={{ marginTop: 16 }}>
                            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('marketplace:admin.moderatorNote')} />
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button type="button" className="sp-btn sp-btn--primary" onClick={() => modAction(selectedReq.id, 'approve')}>{t('marketplace:admin.approve')}</button>
                                <button type="button" className="sp-btn sp-btn--outline" onClick={() => modAction(selectedReq.id, 'docs')}>{t('marketplace:admin.requestDocs')}</button>
                                <button type="button" className="sp-btn sp-btn--outline" onClick={() => modAction(selectedReq.id, 'reject')}>{t('marketplace:admin.reject')}</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {tab === 'analytics' && analytics && (
                <div className="sp-panel">
                    <p>{t('marketplace:admin.activeProfiles')}: <strong>{analytics.activeProfiles}</strong></p>
                    <p>{t('marketplace:admin.views')}: <strong>{analytics.views}</strong></p>
                    <p>{t('marketplace:admin.inquiries')}: <strong>{analytics.inquiries}</strong></p>
                    <p>{t('marketplace:admin.revenue')}: <strong>{analytics.revenue} ₽</strong></p>
                </div>
            )}
        </OwnerPageLayout>
    );
}
